import { useMemo } from 'react'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'

import { isContentfulPreviewFrame } from '../helpers/isContentfulPreviewFrame'
import { overlayContentfulFields } from '../helpers/overlayContentfulFields'
import { useContentfulPreviewAssets } from './useContentfulPreviewAssets'
import { useContentfulPreviewEmbeds } from './useContentfulPreviewEmbeds'
import { useContentfulPreviewEntries } from './useContentfulPreviewEntries'

/**
 * Applies edits made in the Contentful live preview to a Gatsby query result.
 *
 * The editor merges keystrokes into Content Preview API entries and only into those - it has no way
 * to write into the shape `gatsby-source-contentful` produces. So the untransformed preview entries
 * are what the SDK subscribes to, and their values are copied onto the build time data afterwards.
 * Reshaping before the SDK sees the data is what stops live updates working at all.
 *
 * Assets ride alongside rather than through the SDK: the editor only ever edits the *entry* field
 * that points at an asset, so what changes live is the Link, and the asset it now targets is looked
 * up separately by `useContentfulPreviewAssets`. Each preview asset arrives with its
 * `gatsbyImageData` already built by the develop proxy, so an image swap needs no re-source.
 *
 * `useContentfulPreviewEmbeds` does the same for entries embedded in rich text, which are not in
 * the page's include graph when they were added after load. Its entries go **before** the live
 * ones: `overlayContentfulFields` keys them by id into a Map, where later wins, so this is what
 * keeps an entry the SDK is actively updating ahead of a fetched copy of the same entry.
 *
 * Outside the preview iframe nothing is fetched or subscribed and the data passes straight through.
 */
export const useLiveContentfulData = <T>(data: T, pageContentfulId: string): T => {
  const { entries: previewEntries, assets: previewAssets } = useContentfulPreviewEntries(pageContentfulId)
  const skip = !isContentfulPreviewFrame()
  const liveEntries = useContentfulLiveUpdates(previewEntries, { skip })
  const embeds = useContentfulPreviewEmbeds(liveEntries)
  const assets = useContentfulPreviewAssets(previewAssets, liveEntries)

  const entries = useMemo(
    () => (embeds.entries.length ? [...embeds.entries, ...liveEntries] : liveEntries),
    [liveEntries, embeds.entries],
  )

  const allAssets = useMemo(
    () => (embeds.assets.length ? [...embeds.assets, ...assets] : assets),
    [assets, embeds.assets],
  )

  return useMemo(() => overlayContentfulFields(data, entries, allAssets), [data, entries, allAssets])
}
